import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import TabButton from 'components/Button/TabButton'
import ErrorView from 'components/ErrorView'
import Spacer from 'components/Spacer'
import { useAppSelector } from 'state/hooks'
import { selectIsSubAccountMode } from 'state/wallet/selectors'
import media from 'styles/media'

import FundTab from './components/FundTab'
import ManageTab from './components/ManageTab'
import WithdrawTab from './components/WithdrawTab'
import useAbstractionInfo from './hooks/useAbstractionInfo'

export enum TransferType {
	Manage = 'manage',
	Fund = 'fund',
	Withdraw = 'withdraw',
}

const OneClickTradingTab: FC = () => {
	const { t } = useTranslation()
	const [transferType, setTransferType] = useState<TransferType>(TransferType.Manage)
	const isSubAccountMode = useAppSelector(selectIsSubAccountMode)
	const { isLowBalance, isDelegated } = useAbstractionInfo()

	const TabMap = {
		[TransferType.Manage]: {
			key: 'manage',
			component: <ManageTab changeTab={setTransferType} />,
		},
		[TransferType.Fund]: {
			key: 'fund',
			component: <FundTab />,
		},
		[TransferType.Withdraw]: {
			key: 'withdraw',
			component: <WithdrawTab />,
		},
	} as const

	useEffect(() => {
		if (isLowBalance && isDelegated) {
			setTransferType(TransferType.Fund)
		}
		// this hook should only run once
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<>
			<TabButtons>
				{Object.entries(TabMap).map(([key, { key: title }]) => {
					return (
						<TabButton
							key={key}
							title={title}
							active={transferType === key}
							isRounded
							style={transferType !== key ? { borderColor: 'transparent' } : undefined}
							onClick={() => setTransferType(key as TransferType)}
						/>
					)
				})}
			</TabButtons>
			<Spacer height={20} />
			{TabMap[transferType].component}
			{isSubAccountMode && (
				<ErrorView
					messageType="error"
					message={t('futures.market.trade.confirmation.modal.disabled-delegation-trading')}
					containerStyle={{ margin: '16px 0 0 0' }}
				/>
			)}
		</>
	)
}

const TabButtons = styled.div`
	display: flex;

	& > button:not(:last-of-type) {
		margin-right: 25px;
	}

	${media.lessThan('md')`
		justify-content: flex-start;
	`}
`

export default OneClickTradingTab
